import { isLoggedInAtom, isLoggingOutAtom, userInfoAtom } from 'app/atoms';
import { useStore } from 'jotai';
import type { PublishPromptType } from 'app/atoms/ui';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { useSocialFeedFeature } from 'app/hooks/use-social-feed-feature';
import { useCanUseServices, useSimplifiedServices } from 'app/network/category';
import { useLoansWithoutPosts } from 'app/network/post';
import { useUser } from 'app/network/user';
import { ListingSubtype, ListingType } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { DynamicFeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { useEffect, useEffectEvent, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsExpired } from 'app/hooks/use-is-expired';
import { useAuthorize } from 'app/hooks/use-authorize';
import {
  canUseCentralizedCreation,
  canUseOfferHub,
  navigateToLegacyQuickPublishFlow,
  navigateToQuickPublishFlow,
} from 'app/screens/intro/navigator-helper';
import type { NewScreenParams } from './index';
import { RenderedButton1, RenderedButton2 } from './prompt-button';

const PROMPT_HIDE_ANIMATION_MS = 300;

export function useNewListingPromptContent({
  state,
  newScreenParams,
}: {
  state: [boolean | PublishPromptType, Dispatch<SetStateAction<boolean | PublishPromptType>>];
  newScreenParams?: NewScreenParams;
}) {
  const { t } = useTranslation('new');
  const navigation = useNavigation();
  const store = useStore();

  const [isNewListingPromptVisible, setIsNewListingPromptVisible] = state;
  const [listingType, setListingType] = useState<ListingType | undefined>(newScreenParams?.defaultValues?.type);
  const [isChoosingPostLoan, setIsChoosingPostLoan] = useState(isNewListingPromptVisible === 'post');
  const hideResetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { canUseSocialFeed } = useSocialFeedFeature();
  const loansWithoutPostsQuery = useLoansWithoutPosts(canUseSocialFeed && isChoosingPostLoan);

  const canUseServices = useCanUseServices();
  const simplifiedServices = useSimplifiedServices();
  const shouldOpenRequestDirectly = isWhiteLabel(WhiteLabel.HauteGaspesie);

  const { data: user, isPending: isUserPending } = useUser();
  const canUseDonations = Boolean(user?.canPublishDonations);
  const canUseBulkListingCreation =
    !isUserPending && isFeatureEnabled(DynamicFeatureFlag.BulkListingCreation, user?.featureFlags);
  const centralizedCreation = canUseCentralizedCreation(user?.featureFlags, isUserPending);
  const hasOfferHubAccess = canUseOfferHub(user?.featureFlags, isUserPending);

  useEffect(() => {
    let cancelled = false;

    if (hideResetTimeout.current) {
      clearTimeout(hideResetTimeout.current);
      hideResetTimeout.current = null;
    }

    if (!isNewListingPromptVisible) {
      // Wait until hide animation completes, then reset.
      // Otherwise, the UI would change while the animation is running, which looks broken.
      hideResetTimeout.current = setTimeout(() => {
        setListingType(undefined);
        setIsChoosingPostLoan(false);
        hideResetTimeout.current = null;
      }, PROMPT_HIDE_ANIMATION_MS);
    } else {
      // Synchronize externally requested prompt steps after the effect has subscribed.
      void Promise.resolve().then(() => {
        if (cancelled) return;

        if (isNewListingPromptVisible === 'post' && canUseSocialFeed) {
          setListingType(undefined);
          setIsChoosingPostLoan(true);
        } else {
          setIsChoosingPostLoan(false);
          if (isNewListingPromptVisible !== true && isNewListingPromptVisible !== 'post' && listingType === undefined) {
            setListingType(isNewListingPromptVisible);
          }
        }
      });
    }

    return () => {
      cancelled = true;
      if (hideResetTimeout.current) {
        clearTimeout(hideResetTimeout.current);
        hideResetTimeout.current = null;
      }
    };
  }, [canUseSocialFeed, isNewListingPromptVisible, listingType]);

  const openNewWithGuard = useOpenNewWithGuard();
  const authorizePublish = useAuthorize('publish');
  const authorizeRequest = useIsExpired();

  const navigateToNew = (type: ListingType, subtype?: ListingSubtype) => {
    authorizePublish(() => {
      const screenParams = newScreenParams ?? {};
      if (screenParams.defaultValues) {
        screenParams.defaultValues.type = type;
        screenParams.defaultValues.subtype = subtype;
      } else {
        screenParams.defaultValues = {
          type,
          subtype,
        };
      }
      if (simplifiedServices && subtype === ListingSubtype.service) {
        screenParams.defaultValues.serviceCategoryId = simplifiedServices;
      }
      setIsNewListingPromptVisible(false);
      setTimeout(() => {
        openNewWithGuard(navigation, screenParams);
      }, PROMPT_HIDE_ANIMATION_MS);
    });
  };

  const navigateToService = () => {
    authorizePublish(() => {
      setIsNewListingPromptVisible(false);
      setTimeout(() => {
        if (simplifiedServices) {
          navigation.navigate(
            'new',
            {
              defaultValues: {
                type: ListingType.request,
                subtype: ListingSubtype.service,
                serviceCategoryId: simplifiedServices,
              },
            },
            { pop: true },
          );
        } else {
          navigation.navigate('serviceCategories', undefined, { pop: true });
        }
      }, PROMPT_HIDE_ANIMATION_MS);
    });
  };

  const navigateToOfferHub = () => {
    authorizePublish(() => {
      setIsNewListingPromptVisible(false);
      setTimeout(() => {
        navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending, newScreenParams);
      }, PROMPT_HIDE_ANIMATION_MS);
    });
  };

  const openCreationTab = useEffectEvent(() => {
    const type =
      isNewListingPromptVisible === ListingType.request
        ? ListingType.request
        : isNewListingPromptVisible === 'post'
          ? undefined
          : listingType;
    const open = () =>
      authorizePublish(() => {
        setIsNewListingPromptVisible(false);
        setTimeout(
          () =>
            navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending, {
              ...newScreenParams,
              creationTab:
                isNewListingPromptVisible === 'post' && canUseSocialFeed
                  ? 'stories'
                  : type === ListingType.request
                    ? 'manual'
                    : newScreenParams?.creationTab,
              defaultValues: { ...newScreenParams?.defaultValues, ...(type ? { type } : {}) },
            }),
          PROMPT_HIDE_ANIMATION_MS,
        );
      });
    if (type === ListingType.request) {
      setIsNewListingPromptVisible(false);
      authorizeRequest(open);
    } else open();
  });
  useEffect(() => {
    if (isNewListingPromptVisible && centralizedCreation) openCreationTab();
  }, [centralizedCreation, isNewListingPromptVisible]);

  const openOfferHubFromPrompt = useEffectEvent(navigateToOfferHub);
  useEffect(() => {
    if (isNewListingPromptVisible && listingType === ListingType.offer && hasOfferHubAccess && !centralizedCreation)
      openOfferHubFromPrompt();
  }, [centralizedCreation, hasOfferHubAccess, isNewListingPromptVisible, listingType]);

  const navigateToQuickOffers = () => {
    authorizePublish(() => {
      const initiatingUserId = store.get(userInfoAtom)?.id;
      setIsNewListingPromptVisible(false);
      setTimeout(() => {
        const currentUser = store.get(userInfoAtom);
        // The sheet animation can outlive logout or an account switch.
        if (
          !store.get(isLoggedInAtom) ||
          store.get(isLoggingOutAtom) ||
          (initiatingUserId !== undefined && currentUser?.id !== initiatingUserId)
        )
          return;
        navigateToLegacyQuickPublishFlow(navigation, { fromBabillard: true, featureFlags: currentUser?.featureFlags });
      }, PROMPT_HIDE_ANIMATION_MS);
    });
  };

  const navigateToBulkOffers = () => {
    authorizePublish(() => {
      setIsNewListingPromptVisible(false);
      setTimeout(() => {
        navigation.navigate('bulkNew', undefined, { pop: true });
      }, PROMPT_HIDE_ANIMATION_MS);
    });
  };

  const navigateToPost = (loanId: string, listingId: string) => {
    authorizePublish(() => {
      setIsNewListingPromptVisible(false);
      setTimeout(() => navigation.navigate('new-post', { loanId, listingId }, { pop: true }), PROMPT_HIDE_ANIMATION_MS);
    });
  };

  const form = (() => {
    if (isChoosingPostLoan && !listingType && canUseSocialFeed) {
      return {
        title: t('prompt.post.title'),
        onBack: () => {
          setIsNewListingPromptVisible(true);
          setIsChoosingPostLoan(false);
        },
        buttons: loansWithoutPostsQuery.data.length
          ? loansWithoutPostsQuery.data.map((loan) => (
              <RenderedButton2
                {...(loan.listing.type === ListingType.request &&
                (!loan.listing.picture || loan.listing.picture.includes('/default/media/'))
                  ? { emoji: '🤝' }
                  : {
                      leading: (
                        <ListingThumbnail
                          hideCorpoBadge
                          picture={loan.listing.picture}
                          size={48}
                          subtype={loan.listing.subtype}
                          type={loan.listing.type}
                        />
                      ),
                    })}
                key={loan.id}
                onPress={() => navigateToPost(loan.id, loan.listingId)}
                text={loan.listing.item.name}
                variant="secondary"
              />
            ))
          : [
              <Text color="text.secondary" key="empty-post-loans" textAlign="center" variant="bodyBase">
                {loansWithoutPostsQuery.isPending ? t('prompt.post.loading') : t('prompt.post.empty')}
              </Text>,
            ],
      };
    }

    if (!listingType) {
      // First question: What type of listing?
      return {
        title: t('prompt.type.title'),
        buttons: [
          <RenderedButton1
            chevron
            color={{ light: '#A0A7FF', default: '#A0A7FF' }}
            emoji="🫴"
            key="offer"
            onPress={() => {
              setIsChoosingPostLoan(false);
              if (hasOfferHubAccess) {
                navigateToOfferHub();
              } else {
                // Keep the controlled prompt open on the legacy offer step.
                // Storing this only in local state can be lost when the native
                // sheet rerenders after the first-step button press.
                setIsNewListingPromptVisible(ListingType.offer);
                setListingType(ListingType.offer);
              }
            }}
            testID="new-listing-prompt-type-offer"
            text={t('prompt.type.offer')}
          />,

          <RenderedButton1
            chevron={!shouldOpenRequestDirectly && (canUseDonations || canUseServices)}
            color={{ light: '#A0A7FF', default: '#A0A7FF' }}
            emoji="👋"
            key="request"
            onPress={() => {
              if (!shouldOpenRequestDirectly && (canUseDonations || canUseServices)) {
                setIsChoosingPostLoan(false);
                setListingType(ListingType.request);
              } else {
                navigateToNew(ListingType.request);
              }
            }}
            text={t('prompt.type.request')}
          />,

          ...(canUseSocialFeed
            ? [
                <RenderedButton1
                  chevron
                  color={{ light: '#A0A7FF', default: '#A0A7FF' }}
                  emoji="✍️"
                  key="post"
                  onPress={() => {
                    setListingType(undefined);
                    setIsChoosingPostLoan(true);
                  }}
                  testID="new-listing-prompt-type-post"
                  text={t('prompt.type.post')}
                />,
              ]
            : []),
        ],
      };
    } else {
      if (listingType === ListingType.offer) {
        return {
          title: t('prompt.offer.title'),
          onBack: () => {
            setIsNewListingPromptVisible(true);
            setListingType(undefined);
          },
          buttons: [
            ...(canUseBulkListingCreation
              ? [
                  <RenderedButton2
                    emoji="📷"
                    key="bulk"
                    onPress={() => navigateToBulkOffers()}
                    text={t('prompt.offer.bulk')}
                    variant="secondary"
                  />,
                ]
              : []),
            <RenderedButton2
              emoji="⚡"
              key="many"
              onPress={() => navigateToQuickOffers()}
              text={t('prompt.offer.many')}
              variant="secondary"
            />,

            ...(canUseDonations && !isWhiteLabel(WhiteLabel.HauteGaspesie)
              ? [
                  <RenderedButton2
                    emoji="🎁"
                    key="don"
                    onPress={() => navigateToNew(ListingType.offer, ListingSubtype.donation)}
                    text={t('prompt.offer.donation')}
                    variant="secondary"
                  />,
                ]
              : []),
            <RenderedButton2
              emoji="📸"
              key="one"
              onPress={() => navigateToNew(ListingType.offer)}
              testID="new-listing-prompt-offer-one"
              text={t('prompt.offer.one')}
              variant="primary"
            />,
          ],
        };
      }
      // listingType === ListingType.request
      if (canUseDonations || canUseServices) {
        return {
          title: t('prompt.request.title'),
          onBack: () => {
            setIsNewListingPromptVisible(true);
            setListingType(undefined);
          },
          buttons: [
            ...(canUseDonations
              ? [
                  <RenderedButton2
                    emoji="🎁"
                    key="don"
                    onPress={() => navigateToNew(ListingType.request, ListingSubtype.donation)}
                    text={t('prompt.request.donation')}
                    variant="secondary"
                  />,
                ]
              : []),
            ...(canUseServices
              ? [
                  <RenderedButton2
                    emoji="🙏"
                    key="serviceRequest"
                    onPress={() => navigateToService()}
                    text={t('prompt.request.service')}
                    variant="secondary"
                  />,
                ]
              : []),
            <RenderedButton2
              emoji="📸"
              key="one"
              onPress={() => navigateToNew(ListingType.request)}
              text={t('prompt.request.one')}
              variant="primary"
            />,
          ],
        };
      }
    }
  })();

  return { form: centralizedCreation ? undefined : form, centralizedCreation };
}
